// ============================================
// Media Store - Movies, TV Shows, Search
// Single Responsibility: Media data management
// ============================================

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { MediaItem, TMDBMovie, TMDBTVShow } from '../types';

interface MediaState {
    // Data
    movies: TMDBMovie[];
    tvShows: TMDBTVShow[];
    trending: MediaItem[];
    searchResults: MediaItem[];
    searchQuery: string;

    // Loading states
    moviesLoading: boolean;
    trendingLoading: boolean;
    searchLoading: boolean;

    // Actions
    setMovies: (movies: TMDBMovie[]) => void;
    setTVShows: (tvShows: TMDBTVShow[]) => void;
    setTrending: (trending: MediaItem[]) => void;
    setSearchResults: (results: MediaItem[]) => void;
    setSearchQuery: (query: string) => void;
    setMoviesLoading: (loading: boolean) => void;
    setTrendingLoading: (loading: boolean) => void;
    setSearchLoading: (loading: boolean) => void;
    clearSearch: () => void;
    reset: () => void;
}

const initialState = {
    movies: [],
    tvShows: [],
    trending: [],
    searchResults: [],
    searchQuery: '',
    moviesLoading: false,
    trendingLoading: false,
    searchLoading: false,
};

export const useMediaStore = create<MediaState>()(
    immer((set) => ({
        ...initialState,

        setMovies: (movies) => set((state) => {
            state.movies = movies;
        }),

        setTVShows: (tvShows) => set((state) => {
            state.tvShows = tvShows;
        }),

        setTrending: (trending) => set((state) => {
            state.trending = trending;
        }),

        setSearchResults: (results) => set((state) => {
            state.searchResults = results;
        }),

        setSearchQuery: (query) => set((state) => {
            state.searchQuery = query;
        }),

        setMoviesLoading: (loading) => set((state) => {
            state.moviesLoading = loading;
        }),

        setTrendingLoading: (loading) => set((state) => {
            state.trendingLoading = loading;
        }),

        setSearchLoading: (loading) => set((state) => {
            state.searchLoading = loading;
        }),

        clearSearch: () => set((state) => {
            state.searchResults = [];
            state.searchQuery = '';
        }),

        reset: () => set(initialState),
    }))
);
